/**
 * Summary Page
 *
 * Executive KPI cards - the hero view of the founder dashboard.
 * Shows high-level platform metrics at a glance.
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { founderDashboardAPI, thirdPartyAnalyticsAPI } from '../../services/api';
import { useEnvironment } from '../../context/EnvironmentContext';
import KPICard from '../../components/founder-dashboard/KPICard';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  BoltIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  CreditCardIcon,
  FireIcon,
  CameraIcon,
  CloudIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';

// Service Health Card Component
function ServiceHealthCard({ title, icon: Icon, status, link, loading }) {
  const statusStyles = {
    healthy: 'border-green-200 bg-green-50',
    warning: 'border-yellow-200 bg-yellow-50',
    critical: 'border-red-200 bg-red-50',
    unknown: 'border-gray-200 bg-gray-50',
  };

  const statusDot = {
    healthy: 'bg-green-500',
    warning: 'bg-yellow-500',
    critical: 'bg-red-500',
    unknown: 'bg-gray-400',
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 animate-pulse">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-16"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={link}
      className={`block rounded-xl border-2 p-4 transition-all hover:shadow-md ${statusStyles[status]}`}
    >
      <div className="flex items-center gap-3">
        <div className="p-2 bg-white rounded-lg shadow-sm">
          <Icon className="w-6 h-6 text-gray-600" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-gray-900">{title}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`w-2 h-2 rounded-full ${statusDot[status]}`}></span>
            <span className="text-sm text-gray-600 capitalize">{status}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function SummaryPage() {
  const { currentEnv } = useEnvironment();
  const [data, setData] = useState(null);
  const [thirdPartyHealth, setThirdPartyHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null); // Track which env the data/error belongs to

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, healthRes] = await Promise.all([
        founderDashboardAPI.getSummary(),
        thirdPartyAnalyticsAPI.getServiceHealth().catch(() => ({ data: null })),
      ]);
      setData(summaryRes.data);
      setThirdPartyHealth(healthRes.data);
      setDataEnv(currentEnv); // Mark data as belonging to current env
    } catch (err) {
      console.error('[SummaryPage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
      setDataEnv(currentEnv); // Mark error as belonging to current env
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear all state and refetch when environment changes
    setData(null);
    setError(null);
    fetchData();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentEnv]);

  // Only show error if it's from the current environment (prevents flash of stale error)
  if (error && dataEnv === currentEnv) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">Error loading summary</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const trends = data?.trends || {};

  // Compute groups subtitle with week-over-week comparison
  const groupsSubtitle = useMemo(() => {
    if (kpis.activeGroupsThisWeek === undefined) return null;
    const diff = kpis.activeGroupsThisWeek - (kpis.activeGroupsLastWeek || 0);
    const diffStr = diff > 0 ? `+${diff}` : diff < 0 ? `${diff}` : '±0';
    return `${kpis.activeGroupsThisWeek} groups that used Flock this week (${diffStr} from last week)`;
  }, [kpis.activeGroupsThisWeek, kpis.activeGroupsLastWeek]);

  const attendanceGroupsSubtitle = useMemo(() => {
    if (kpis.attendanceGroupsThisWeek === undefined) return null;
    const diff = kpis.attendanceGroupsThisWeek - (kpis.attendanceGroupsLastWeek || 0);
    const diffStr = diff > 0 ? `+${diff}` : `${diff}`;
    return `${diffStr} vs last week (${kpis.attendanceGroupsLastWeek || 0})`;
  }, [kpis.attendanceGroupsThisWeek, kpis.attendanceGroupsLastWeek]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Executive Summary</h2>
          <p className="text-gray-500 mt-1">
            Platform-wide KPIs at a glance
          </p>
        </div>
        <RefreshIndicator
          lastUpdated={data?.timestamp}
          onRefresh={fetchData}
          loading={loading}
        />
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <KPICard
          title="Active Organizations"
          value={kpis.activeOrganizations}
          change={trends.orgsChange}
          icon={BuildingOfficeIcon}
          loading={loading}
        />
        <KPICard
          title="Total Members"
          value={kpis.totalMembers}
          change={trends.membersChange}
          icon={UsersIcon}
          loading={loading}
        />
        <KPICard
          title="Total Groups"
          value={kpis.totalGroups}
          change={trends.groupsChange}
          subtitle={groupsSubtitle}
          icon={UserGroupIcon}
          loading={loading}
        />
        <KPICard
          title="Monthly Recurring Revenue"
          value={kpis.mrr}
          change={trends.mrrChange}
          format="currency"
          icon={CurrencyDollarIcon}
          loading={loading}
        />
        <KPICard
          title="Trial Conversion Rate"
          value={kpis.trialConversionRate}
          format="percent"
          icon={ChartBarIcon}
          loading={loading}
        />
        <KPICard
          title="Used Flock this week (church)"
          value={kpis.weeklyActiveUsers}
          icon={BoltIcon}
          subtitle="Habit WAU · church members"
          loading={loading}
        />
        <KPICard
          title="Groups that took attendance"
          value={kpis.attendanceGroupsThisWeek}
          icon={CalendarDaysIcon}
          subtitle={attendanceGroupsSubtitle || 'Last 7 Chicago days · church'}
          loading={loading}
        />
      </div>

      {/* Third-Party Service Health */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Third-Party Service Health
          </h3>
          <Link
            to="/alerts"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            View All Alerts →
          </Link>
        </div>

        {/* Alert Banner */}
        {thirdPartyHealth?.alerts?.unacknowledged > 0 && (
          <Link
            to="/alerts"
            className="block bg-red-50 border border-red-200 rounded-xl p-4 hover:bg-red-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <BellAlertIcon className="w-6 h-6 text-red-600" />
              <div>
                <p className="font-medium text-red-800">
                  {thirdPartyHealth.alerts.unacknowledged} Unacknowledged Alert{thirdPartyHealth.alerts.unacknowledged > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-red-600">
                  Click to review and acknowledge alerts
                </p>
              </div>
            </div>
          </Link>
        )}

        {/* Service Health Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <ServiceHealthCard
            title="SendGrid"
            icon={EnvelopeIcon}
            status={thirdPartyHealth?.services?.sendgrid || 'unknown'}
            link="/sendgrid"
            loading={loading}
          />
          <ServiceHealthCard
            title="Stripe"
            icon={CreditCardIcon}
            status={thirdPartyHealth?.services?.stripe || 'unknown'}
            link="/stripe"
            loading={loading}
          />
          <ServiceHealthCard
            title="Firestore"
            icon={FireIcon}
            status={thirdPartyHealth?.services?.firestore || 'unknown'}
            link="/firestore"
            loading={loading}
          />
          <ServiceHealthCard
            title="Rekognition"
            icon={CameraIcon}
            status={thirdPartyHealth?.services?.rekognition || 'unknown'}
            link="/rekognition"
            loading={loading}
          />
          <ServiceHealthCard
            title="R2 Storage"
            icon={CloudIcon}
            status={thirdPartyHealth?.services?.r2 || 'unknown'}
            link="/r2-storage"
            loading={loading}
          />
        </div>
      </div>

      {/* Additional context */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <p className="text-sm text-blue-800">
          <strong>Tip:</strong> Click on <strong>Engagement</strong> for detailed
          prayer, attendance, and event metrics. Click on{' '}
          <strong>Organizations</strong> to drill down into individual churches.
        </p>
      </div>
    </div>
  );
}
